/**
 * Response-expectation class pill (spec §2.2) — click-to-override.
 * Only 'narrative' items count toward the coverage denominator; overriding
 * sets response_expectation_source='manual' so re-extraction merges respect it.
 */

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Check } from 'lucide-react'
import type { ResponseExpectation } from '@/types/extraction'
import { expectationConfig, EXPECTATION_ORDER } from '@/components/requirement-coverage/coverage-status'
import { cn } from '@/lib/utils'

const EXPECTATION_DESCRIPTIONS: Record<ResponseExpectation, string> = {
  narrative: 'Addressed in written prose — counts toward coverage',
  evidence: 'Certificate / document to attach',
  form: 'Tender-supplied form to complete',
  post_award: 'Only applies after contract award',
}

interface ExpectationPillProps {
  value: ResponseExpectation
  readOnly?: boolean
  onChange: (next: ResponseExpectation) => void
}

export function ExpectationPill({ value, readOnly, onChange }: ExpectationPillProps) {
  const config = expectationConfig[value]
  const pill = (
    <span
      className={cn(
        'inline-flex items-center rounded-md border border-border bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground',
        !readOnly && 'cursor-pointer hover:bg-accent hover:text-foreground'
      )}
      title={EXPECTATION_DESCRIPTIONS[value]}
    >
      {config.label}
    </span>
  )

  if (readOnly) return pill

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" aria-label={`Response expectation: ${config.label}`}>
          {pill}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        {EXPECTATION_ORDER.map((option) => (
          <DropdownMenuItem
            key={option}
            onClick={() => option !== value && onChange(option)}
            className="flex items-start gap-2"
          >
            <Check className={cn('mt-0.5 h-3.5 w-3.5 shrink-0', option === value ? 'opacity-100' : 'opacity-0')} />
            <span>
              <span className="block text-xs font-medium">{expectationConfig[option].label}</span>
              <span className="block text-[11px] text-muted-foreground">
                {EXPECTATION_DESCRIPTIONS[option]}
              </span>
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
